import { create } from 'zustand';
import {
  getNotificationErrorMessage,
  notificationService,
} from '../services/notificationService';
import { NotificationItem, NotificationTab } from '../types/notification';

const DEFAULT_PAGE_SIZE = 10;

interface FetchNotificationsOptions {
  tab?: NotificationTab;
  page?: number;
  append?: boolean;
}

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  currentPage: number;
  totalPages: number;
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: (options?: FetchNotificationsOptions) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  notifications: [],
  unreadCount: 0,
  currentPage: 1,
  totalPages: 0,
  total: 0,
  isLoading: false,
  isLoadingMore: false,
  error: null,

  fetchUnreadCount: async () => {
    try {
      const count = await notificationService.getUnreadCount();
      set({ unreadCount: count });
    } catch {
      // Keep the last known badge count if the unread-count request fails.
    }
  },

  fetchNotifications: async (options = {}) => {
    const tab = options.tab ?? 'all';
    const page = options.page ?? 1;
    const append = options.append ?? false;

    set({
      isLoading: !append,
      isLoadingMore: append,
      error: null,
    });

    try {
      const response = await notificationService.getNotifications({
        tab,
        page,
        limit: DEFAULT_PAGE_SIZE,
      });

      set(state => ({
        notifications: append
          ? [...state.notifications, ...response.data]
          : response.data,
        currentPage: response.metaData.page,
        totalPages: response.metaData.totalPages,
        total: response.metaData.total,
        isLoading: false,
        isLoadingMore: false,
      }));
    } catch (error) {
      set({
        isLoading: false,
        isLoadingMore: false,
        error: getNotificationErrorMessage(error, 'Failed to load notifications.'),
      });
    }
  },

  markAllAsRead: async () => {
    set(state => ({
      notifications: state.notifications.map(notification => ({
        ...notification,
        isNew: false,
      })),
      unreadCount: 0,
    }));

    try {
      await notificationService.markAllAsRead();
      await get().fetchUnreadCount();
    } catch (error) {
      await get().fetchNotifications({ tab: 'all', page: 1 });
      await get().fetchUnreadCount();
      throw error;
    }
  },

  markAsRead: async (notificationId: number) => {
    set(state => ({
      notifications: state.notifications.map(notification =>
        notification.notificationId === notificationId
          ? { ...notification, isNew: false }
          : notification
      ),
      unreadCount: Math.max(
        0,
        state.unreadCount -
          (state.notifications.find(item => item.notificationId === notificationId)?.isNew
            ? 1
            : 0)
      ),
    }));

    try {
      await notificationService.markAsRead(notificationId);
      await get().fetchUnreadCount();
    } catch (error) {
      await get().fetchNotifications({ tab: 'all', page: get().currentPage });
      await get().fetchUnreadCount();
      throw error;
    }
  },

  deleteNotification: async (notificationId: number) => {
    const removed = get().notifications.find(item => item.notificationId === notificationId);

    set(state => ({
      notifications: state.notifications.filter(
        item => item.notificationId !== notificationId
      ),
      total: Math.max(0, state.total - 1),
      unreadCount:
        removed?.isNew && state.unreadCount > 0 ? state.unreadCount - 1 : state.unreadCount,
    }));

    try {
      await notificationService.deleteNotification(notificationId);
      await get().fetchUnreadCount();
    } catch (error) {
      await get().fetchNotifications({ tab: 'all', page: get().currentPage });
      await get().fetchUnreadCount();
      throw error;
    }
  },
}));
